class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		/** On /search?q=…, the q from the URL when the page loaded (see connectedCallback). */
		this._committedUrlQuery = null;
		this.overlay = document.querySelector(".header__overlay");
		this.body = document.querySelector("body");
		this.headerTop = document.querySelector(".header__top");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.setupEventListeners();
	}

	connectedCallback() {
		this._committedUrlQuery = this.getCommittedQueryFromUrl();
	}

	/** True when the storefront URL is the search results page (handles localized paths). */
	isSearchResultsPagePath() {
		if (window.routes && window.routes.search_url) {
			try {
				const searchPath = new URL(window.routes.search_url, window.location.origin)
					.pathname;
				return (
					window.location.pathname === searchPath ||
					window.location.pathname.endsWith(searchPath)
				);
			} catch (e) {
				/* fall through */
			}
		}
		return /\/search\/?$/.test(window.location.pathname);
	}

	/** q param on the search results page, or null when not on that page. */
	getCommittedQueryFromUrl() {
		if (!this.isSearchResultsPagePath()) return null;
		try {
			const params = new URLSearchParams(window.location.search);
			return (params.get("q") || "").trim();
		} catch (e) {
			return null;
		}
	}

	/** Compact / template dropdown: hide predictions when the field still matches the URL query (post-navigation). */
	isTemplateMatchingCommittedUrl(trimmedQuery) {
		if (!this.predictiveSearchResults?.classList.contains("predictive-search--search-template")) {
			return false;
		}
		if (!this.isSearchResultsPagePath()) return false;
		if (this._committedUrlQuery == null || this._committedUrlQuery.length === 0) {
			return false;
		}
		return trimmedQuery === this._committedUrlQuery;
	}

	suppressTemplateDropdownForCommittedQuery(trimmedQuery) {
		if (!this.isTemplateMatchingCommittedUrl(trimmedQuery)) return false;
		this.searchTerm = trimmedQuery;
		this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		this.querySelector("#predictive-search-results")?.remove();
		this.removeAttribute("results");
		this.removeAttribute("loading");
		const selected = this.querySelector('[aria-selected="true"]');
		if (selected) selected.setAttribute("aria-selected", "false");
		this.input.setAttribute("aria-activedescendant", "");
		this.closeResults(false);
		return true;
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	getPredictiveSectionId() {
		return this.getAttribute("data-predictive-section-id") || "predictive-search";
	}

	getResourceTypesParam() {
		return (
			this.getAttribute("data-predictive-resource-types") ||
			"product,collection,article,page"
		);
	}

	emptyDefaultPanelEl() {
		return this.querySelector("[data-predictive-empty-panel]");
	}

	hideEmptyDefaultPanel() {
		const p = this.emptyDefaultPanelEl();
		if (p) p.hidden = true;
	}

	showEmptyDefaultPanel() {
		const p = this.emptyDefaultPanelEl();
		if (!p) return;
		p.hidden = false;
		this.removeAttribute("loading");
		this.open();
	}

	cacheKeyFor(searchTerm) {
		return (
			searchTerm.replace(/ /g, "-").toLowerCase() +
			"::" +
			this.getPredictiveSectionId()
		);
	}

	onChange() {
		//super.onChange();
		const newSearchTerm = this.getQuery();
		if (this.suppressTemplateDropdownForCommittedQuery(newSearchTerm)) {
			return;
		}
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
			this.querySelector("#predictive-search-results")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			if (this.emptyDefaultPanelEl()) {
				this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
				this.querySelector("#predictive-search-results")?.remove();
				this.removeAttribute("results");
				this.removeAttribute("loading");
				const selected = this.querySelector('[aria-selected="true"]');
				if (selected) selected.setAttribute("aria-selected", "false");
				this.input.setAttribute("aria-activedescendant", "");
				this.showEmptyDefaultPanel();
			} else {
				this.close(true);
			}
			return;
		}

		this.hideEmptyDefaultPanel();

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector(
				'li[aria-selected="true"], button[aria-selected="true"]',
			)
		) {
			event.preventDefault();
		}
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			this.overlay.classList.add("visible");
			this.headerTop.classList.add("header__top--search");
			//this.body.classList.add("overflow-hidden");
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
		}

		if (!currentSearchTerm.length) {
			if (this.emptyDefaultPanelEl()) {
				this.showEmptyDefaultPanel();
			}
			return;
		}

		if (this.suppressTemplateDropdownForCommittedQuery(currentSearchTerm)) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			this.headerTop.classList.remove("header__top--search");
			this.overlay.classList.remove("visible");
			//this.body.classList.remove("overflow-hidden");
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		});
	}

	onBlur() {
		this.headerInput?.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);

		switch (event.code) {
			case "Escape":
				event.preventDefault();
				this.closeResults(true);
				if (this.promoModal && this.classList.contains("search-modal__form")) {
					this.promoModal.classList.add("search__modal--hidden");
				}
				break;
			default:
				break;
		}

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onKeydown(event) {
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
			this.switchOption(event.code === "ArrowUp" ? "up" : "down");
			return;
		}
		if (event.code === "Enter") {
			const selected = this.querySelector(
				'li[aria-selected="true"], button[aria-selected="true"]',
			);
			if (selected) {
				event.preventDefault();
				this.selectOption();
			}
		}
	}

	/**
	 * Visible predictive rows (compact + modal listbox), DOM order. Hidden tab panels excluded via offsetParent.
	 */
	getSelectableListItems() {
		if (!this.predictiveSearchResults) return [];
		const nodes = this.predictiveSearchResults.querySelectorAll(
			"li.predictive-search__list-item[role='option']",
		);
		return Array.from(nodes).filter((el) => {
			if (el.offsetParent === null) return false;
			return !!(el.querySelector("a[href]") || el.querySelector("a"));
		});
	}

	switchOption(direction) {
		if (!this.getAttribute("open") || !this.getQuery().length) return;

		const items = this.getSelectableListItems();
		if (!items.length) return;

		const moveUp = direction === "up";
		const selected = this.querySelector('li[aria-selected="true"]');
		let idx = selected ? items.indexOf(selected) : -1;

		if (moveUp && idx === -1) {
			idx = items.length - 1;
		} else if (!moveUp && idx === -1) {
			idx = 0;
		} else if (moveUp) {
			idx = idx <= 0 ? items.length - 1 : idx - 1;
		} else {
			idx = idx >= items.length - 1 ? 0 : idx + 1;
		}

		const next = items[idx];
		if (!next) return;

		if (selected) selected.setAttribute("aria-selected", "false");
		next.setAttribute("aria-selected", "true");
		const id = next.id;
		if (id) {
			this.input.setAttribute("aria-activedescendant", id);
		}
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		if (this.statusElement) {
			this.statusElement.textContent = "";
		}

		next.scrollIntoView({ block: "nearest", inline: "nearest" });
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedBtn = this.querySelector('button[aria-selected="true"]');
		if (selectedBtn) {
			selectedBtn.click();
			return;
		}
		const li = this.querySelector('li[aria-selected="true"]');
		if (!li) return;
		const link =
			li.querySelector("a.predictive-search-compact__link") ||
			li.querySelector("a.card__link") ||
			li.querySelector("a[href]");
		if (link) link.click();
	}

	getSearchResults(searchTerm) {
		this.hideEmptyDefaultPanel();
		const queryKey = this.cacheKeyFor(searchTerm);
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		const types = this.getResourceTypesParam();
		const sectionId = this.getPredictiveSectionId();
		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(
				searchTerm,
			)}&resources[type]=${types}&section_id=${encodeURIComponent(sectionId)}`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = PredictiveSearch.extractSectionInnerHTML(text);
				// Keep cache aligned per section + API shape (modal vs compact template)
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						if (
							predictiveSearchInstance.getPredictiveSectionId() === sectionId
						) {
							predictiveSearchInstance.cachedResults[queryKey] =
								resultsMarkup;
						}
					},
				);
				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				if (typeof colorSwatches === 'function') {
					colorSwatches();
				}
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});
	}

	static extractSectionInnerHTML(htmlText) {
		const doc = new DOMParser().parseFromString(htmlText, "text/html");
		const wrapper =
			doc.querySelector('[id^="shopify-section-"]') ||
			doc.querySelector("shopify-section") ||
			doc.querySelector("[data-shopify-editor-section]");
		if (!wrapper) {
			throw new Error("Predictive search: missing section markup in response");
		}
		return wrapper.innerHTML;
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	applyResultLimit() {
		const limit = parseInt(this.getAttribute("data-result-limit") || "0", 10);
		if (!limit || !this.predictiveSearchResults) return;

		const items = this.predictiveSearchResults.querySelectorAll(
			".predictive-search__list-item",
		);
		items.forEach((el, i) => {
			if (i >= limit) el.remove();
		});

		this.predictiveSearchResults
			.querySelectorAll(".predictive-search__results-list")
			.forEach((ul) => {
				if (!ul.querySelector("li")) {
					const wrap = ul.closest(".predictive-search__pages-wrapper");
					if (wrap) wrap.remove();
					else ul.remove();
				}
			});

		const tabs = this.predictiveSearchResults.querySelector(
			".predictive-search__result-tabs",
		);
		if (tabs) {
			tabs.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				const typeTarget = tab.dataset.typeTarget;
				const list = this.predictiveSearchResults.querySelector(
					`.predictive-search__results-list[data-type="${typeTarget}"]`,
				);
				if (!list || !list.querySelector("li")) tab.remove();
			});
			if (!tabs.querySelector(".predictive-search__result-tab")) tabs.remove();
		}

		this.predictiveSearchResults
			.querySelectorAll(".predictive-search__results-list")
			.forEach((el) => {
				el.classList.remove("active");
			});
		const firstItem = this.predictiveSearchResults.querySelector(
			".predictive-search__results-list li",
		);
		const firstList = firstItem && firstItem.closest(".predictive-search__results-list");
		if (firstList) firstList.classList.add("active");
	}

	renderSearchResults(resultsMarkup) {
		this.hideEmptyDefaultPanel();
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.applyResultLimit();
		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length === 0) return;

		const firstTab = this.querySelector(".predictive-search__result-tab");
		if (!firstTab) {
			if (!results[0].classList.contains("active")) {
				results[0].classList.add("active");
			}
			return;
		}

		results[0].classList.add("active");
		firstTab.classList.add("active");
		this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
			tab.addEventListener("click", (event) => {
				event.preventDefault();
				const typeTarget = tab.dataset.typeTarget;
				this.querySelectorAll(".predictive-search__result-tab").forEach(
					(element) => {
						element.classList.remove("active");
					},
				);
				tab.classList.add("active");

				results.forEach((element) => {
					let resultsType = element.dataset.type;
					if (resultsType == typeTarget) {
						element.classList.add("active");
					} else {
						element.classList.remove("active");
					}
				});
			});
		});
	}

	hidePromoBlock() {
		document.querySelector(".search__categories")
			? document.querySelector(".search__categories").classList.add("hide")
			: false;
		document.querySelector(".search__promo-block")
			? document.querySelector(".search__promo-block").classList.add("hide")
			: false;
		document.querySelector(".search__line")
			? document.querySelector(".search__line").classList.add("hide")
			: false;
	}

	showPromoBlock() {
		document.querySelector(".search__categories")
			? document.querySelector(".search__categories").classList.remove("hide")
			: false;
		document.querySelector(".search__promo-block")
			? document.querySelector(".search__promo-block").classList.remove("hide")
			: false;
		document.querySelector(".search__line")
			? document.querySelector(".search__line").classList.remove("hide")
			: false;
	}


	setLiveRegionResults() {
		this.removeAttribute("loading");
		const live = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);
		const msg = live && live.textContent ? live.textContent.trim() : "";
		if (msg) {
			this.setLiveRegionText(msg);
		}
	}

	//getResultsMaxHeight() {
	//	this.resultsMaxHeight =
	//		window.innerHeight -
	//		document.querySelector("#shopify-section-header").getBoundingClientRect()
	//			.bottom;
	//	return this.resultsMaxHeight;
	//}

	open() {
		//this.predictiveSearchResults.style.maxHeight =
		//	this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.remove("search__modal--hidden");
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		this.hideEmptyDefaultPanel();
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", "false");

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		//this.resultsMaxHeight = false;
		this.predictiveSearchResults.removeAttribute("style");
		if (this.promoModal) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
